import { ProductFamily } from "@custom-types/products"
import Label from "@components/ScenarioTree/Label"
import { ScenarioTreeNode, ScenarioTreeNodeType } from "@components/ScenarioTree/types"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import { CircularProgress, Collapse } from "@mui/material"
import {
  TreeItem2Content,
  TreeItem2Icon,
  TreeItem2IconContainer,
  TreeItem2Provider,
  TreeItem2Root,
  UseTreeItem2Parameters,
  UseTreeItem2Status,
} from "@mui/x-tree-view"
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2/useTreeItem2"
import { RoleHint, useLazyGetProductFamiliesQuery } from "@store/services/baseGraphFamiliesService"
import { HTMLAttributes, forwardRef, useEffect } from "react"

const getContentClassNames = (status: UseTreeItem2Status) => {
  const classNames = ["content"]

  if (status.expanded) classNames.push("Mui-expanded")
  if (status.selected) classNames.push("Mui-selected")
  if (status.focused) classNames.push("Mui-focused")
  if (status.disabled) classNames.push("Mui-disabled")

  return classNames
}

interface NodeItemProps
  extends Omit<UseTreeItem2Parameters, "rootRef">,
    Omit<HTMLAttributes<HTMLLIElement>, "onFocus"> {
  onFamilyFetched: (nodeId: string, families: ProductFamily[]) => void
  prefetchProductItem: string | undefined // node id
  productIdToRefetch: string
  onProductRefetched: () => void
  roleHint: RoleHint | undefined
  selectedFamilyKey: string
}

const NodeItem = forwardRef(function CustomTreeItem(
  {
    id,
    itemId,
    label,
    disabled,
    children,
    onFamilyFetched,
    prefetchProductItem,
    selectedFamilyKey,
    productIdToRefetch,
    onProductRefetched,
    roleHint,
    ...props
  }: NodeItemProps,
  ref: React.Ref<HTMLLIElement>,
) {
  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
    publicAPI,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref })

  const [fetchProductFamilies, { data: productFamilies, isFetching }] =
    useLazyGetProductFamiliesQuery()

  useEffect(() => {
    if (isFetching || !productFamilies) return

    onFamilyFetched(node.id, productFamilies)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [productFamilies, isFetching])

  // needed for F5-refresh to reload the product
  useEffect(() => {
    // refetch makes sense only for product type nodes
    if (node.type !== ScenarioTreeNodeType.PRODUCT) return

    if (node.id !== prefetchProductItem) return

    handleFetchProductFamilies()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [prefetchProductItem])

  // need for creating a new entry in the sidebar when creating a new graph
  useEffect(() => {
    if (node.type !== ScenarioTreeNodeType.PRODUCT) return
    if (node.id !== productIdToRefetch) return

    handleFetchProductFamilies()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [productIdToRefetch])

  const node: ScenarioTreeNode = publicAPI.getItem(itemId)
  const isProductNode = node.type === ScenarioTreeNodeType.PRODUCT
  const isFamilyNode = node.type === ScenarioTreeNodeType.FAMILY

  const updatedStatus: UseTreeItem2Status = {
    ...status,

    // "expandable" controlls visibility of expand/collapse icon. It'll be false if node does not have children.
    // In our case product nodes won't have children until we fetch them, but we still want to show the icon even if not yet fetched.
    expandable: status.expandable || (isProductNode && !isFetching),
  }
  const contentClassName = getContentClassNames(updatedStatus).join(" ")

  const handleFetchProductFamilies = () => {
    if (!isProductNode) return
    if (node.hasFetchedFamilies && !productIdToRefetch) return

    fetchProductFamilies({
      divisionKey: encodeURIComponent(node.divisionKey),
      organisationKey: encodeURIComponent(node.organisationKey),
      productKey: encodeURIComponent(node.productKey),
      roleHint: roleHint,
    })

    if (productIdToRefetch) onProductRefetched()
  }

  return (
    <TreeItem2Provider itemId={itemId}>
      <TreeItem2Root {...getRootProps(props)} onClick={handleFetchProductFamilies}>
        <TreeItem2Content
          {...getContentProps({
            className: contentClassName,
            sx: {
              "&.Mui-selected": {
                backgroundColor: "inherit",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              },
            },
          })}
        >
          <TreeItem2IconContainer {...getIconContainerProps()}>
            <TreeItem2Icon
              status={updatedStatus}
              slots={{ collapseIcon: ExpandLess, expandIcon: ExpandMore }}
            />
            {isFetching && <CircularProgress size={18} />}
          </TreeItem2IconContainer>
          <Label
            {...getLabelProps()}
            sx={{
              fontWeight: isProductNode ? 600 : 400,
              color:
                isFamilyNode && selectedFamilyKey === node.fullFamilyKey ? "error.500" : "inherit",
            }}
          />
        </TreeItem2Content>
        {children && <Collapse {...getGroupTransitionProps()} sx={{ pl: "5px" }} />}
      </TreeItem2Root>
    </TreeItem2Provider>
  )
})

export default NodeItem
